import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import TransactionProofImage from './TransactionProofImage';
import { SidebarContext } from './SidebarContext';
import {
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels);

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const StatCard = ({ label, value, icon: Icon, tone }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    blue: 'bg-brand-50 text-brand-700 border-brand-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-extrabold uppercase tracking-widest opacity-75">{label}</p>
          <p className="mt-2 truncate text-xl font-black text-slate-950">{value}</p>
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const WorkerDashboard = () => {
  const { isSidebarOpen } = useContext(SidebarContext);
  const [netBalance, setNetBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);

  const totals = useMemo(() => {
    const credit = transactions.reduce((sum, txn) => sum + (Number(txn.credit_amount ?? txn.credit_amt) || 0), 0);
    const debit = transactions.reduce((sum, txn) => sum + (Number(txn.debit_amount ?? txn.debit_amt) || 0), 0);
    return { credit, debit, count: transactions.length };
  }, [transactions]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!isLoading && transactions.length > 0) {
      renderCharts();
    }

    return () => {
      pieChartRef.current?.destroy();
      barChartRef.current?.destroy();
      pieChartRef.current = null;
      barChartRef.current = null;
    };
  }, [isLoading, transactions]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError('');

    try {
      const userId = localStorage.getItem('id');
      const userName = localStorage.getItem('user_name');
      const role = localStorage.getItem('role') || 'worker';

      if (!userId || !userName) {
        throw new Error('User session not found');
      }

      const chartResponse = await fetch(`${ApiURL}/PieChart.php?user_id=${userId}&user_name=${encodeURIComponent(userName)}`);
      if (!chartResponse.ok) throw new Error(`Pie chart request failed with status ${chartResponse.status}`);
      const chartData = await chartResponse.json();
      if (chartData.error) throw new Error(chartData.error);
      setNetBalance(Number(chartData.total?.net_balance) || 0);

      const transactionResponse = await fetch(`${ApiURL}/getWorkerTransaction.php?user_id=${userId}&role=${encodeURIComponent(role)}`);
      if (!transactionResponse.ok) throw new Error(`Transaction request failed with status ${transactionResponse.status}`);
      const transactionData = await transactionResponse.json();
      if (transactionData.status !== 'success') throw new Error(transactionData.message || 'Failed to load worker transactions');
      setTransactions(transactionData.transactions || []);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
      setTransactions([]);
      setNetBalance(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getCreditValue = (transaction) => Number(transaction.credit_amount ?? transaction.credit_amt) || 0;
  const getDebitValue = (transaction) => Number(transaction.debit_amount ?? transaction.debit_amt) || 0;

  const renderCharts = () => {
    pieChartRef.current?.destroy();
    barChartRef.current?.destroy();

    const pieCanvas = document.getElementById('workerPieChart');
    const barCanvas = document.getElementById('workerBarChart');
    if (!pieCanvas || !barCanvas) return;

    pieChartRef.current = new ChartJS(pieCanvas.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: ['Credit', 'Debit'],
        datasets: [{
          data: [totals.credit, totals.debit],
          backgroundColor: ['#059669', '#dc2626'],
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
          legend: { position: 'bottom', labels: { color: '#475569', font: { size: 12, weight: '600' } } },
          datalabels: { display: false },
          tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.raw)}` } },
        },
      },
    });

    const recent = transactions.slice(0, 7).reverse();
    barChartRef.current = new ChartJS(barCanvas.getContext('2d'), {
      type: 'bar',
      data: {
        labels: recent.map((txn) => new Date(txn.transaction_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })),
        datasets: [
          {
            label: 'Credit',
            data: recent.map((txn) => getCreditValue(txn)),
            backgroundColor: '#059669',
            borderRadius: 8,
          },
          {
            label: 'Debit',
            data: recent.map((txn) => getDebitValue(txn)),
            backgroundColor: '#dc2626',
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', align: 'end', labels: { color: '#475569', font: { size: 12, weight: '600' } } },
          datalabels: { display: false },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` } },
        },
        scales: {
          y: { beginAtZero: true, grid: { color: '#eef2f7' }, ticks: { color: '#64748b' } },
          x: { grid: { display: false }, ticks: { color: '#64748b' } },
        },
      },
    });
  };

  return (
    <div className="app-shell min-h-screen">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`min-w-0 flex-1 p-3 transition-all duration-300 sm:p-5 lg:p-6 ${isSidebarOpen ? 'md:ml-72' : ''}`}>
          <div className="mx-auto max-w-screen-2xl">
            <div className="page-surface mb-5 flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-widest text-brand-600">Worker Dashboard</p>
                <h1 className="mt-1 text-2xl font-black text-slate-950">My Transaction Overview</h1>
                <p className="mt-1 text-sm text-slate-500">Current balance, recent activity, and latest entries.</p>
              </div>
              <button type="button" onClick={fetchDashboardData} className="pro-button pro-button-secondary">
                <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {isLoading ? (
              <div className="pro-card flex h-64 items-center justify-center text-sm font-semibold text-slate-500">
                Loading dashboard...
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-center text-sm font-semibold text-rose-700">
                {error}
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <StatCard label="Net Balance" value={formatCurrency(netBalance)} icon={WalletIcon} tone={netBalance >= 0 ? 'blue' : 'rose'} />
                  <StatCard label="Total Credit" value={formatCurrency(totals.credit)} icon={ArrowTrendingUpIcon} tone="green" />
                  <StatCard label="Total Debit" value={formatCurrency(totals.debit)} icon={ArrowTrendingDownIcon} tone="rose" />
                  <StatCard label="Transactions" value={totals.count} icon={ClockIcon} tone="slate" />
                </div>

                <div className="grid gap-5 xl:grid-cols-[0.85fr_1.15fr]">
                  <section className="pro-card p-4">
                    <h2 className="text-base font-black text-slate-950">Credit vs Debit</h2>
                    <p className="text-sm text-slate-500">Overall worker ledger split.</p>
                    <div className="mt-4 h-72">
                      <canvas id="workerPieChart" />
                    </div>
                  </section>

                  <section className="pro-card p-4">
                    <h2 className="text-base font-black text-slate-950">Recent Activity</h2>
                    <p className="text-sm text-slate-500">Last seven transactions by date.</p>
                    <div className="mt-4 h-72">
                      <canvas id="workerBarChart" />
                    </div>
                  </section>
                </div>

                <section className="pro-card overflow-hidden">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <h2 className="text-base font-black text-slate-950">Latest Transactions</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-[760px] w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3 text-left">Txn No</th>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-right">Credit</th>
                          <th className="px-4 py-3 text-right">Debit</th>
                          <th className="px-4 py-3 text-left">Particulars</th>
                          <th className="px-4 py-3 text-left">Proof</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.slice(0, 6).map((txn) => (
                          <tr key={txn.transaction_no} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-bold text-slate-950">{txn.transaction_no}</td>
                            <td className="px-4 py-3 text-slate-600">{txn.transaction_date}</td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-700">{formatCurrency(getCreditValue(txn))}</td>
                            <td className="px-4 py-3 text-right font-bold text-rose-700">{formatCurrency(getDebitValue(txn))}</td>
                            <td className="px-4 py-3 text-slate-700">{txn.particuler_to || '-'}</td>
                            <td className="px-4 py-3"><TransactionProofImage path={txn.attachment_path} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </motion.div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default WorkerDashboard;
